import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { getDeliveryOptions } from '../utils/deliveryDate';
import { useDesign } from '../contexts/DesignContext';
import { useLoading } from '../contexts/LoadingContext';
import ProductMediaWithSwatches from '../components/ProductMediaWithSwatches';
import RecentlyViewed from '../components/RecentlyViewed';
import ProductGridSkeleton from '../components/ProductGridSkeleton';

// Banner Carousel Component - Reusable for all sections
const BannerCarousel = ({
  images,
  mobileImages = [],
  links = [],
  fullHeight = false,
  minHeight = 300,
  autoplay = true,
  autoplaySpeed = 4000,
  direction = 'rtl',
  swipeEnabled = true
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  const goNext = useCallback(() => {
    if (images.length <= 1) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    if (images.length <= 1) return;
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (images.length === 0) {
      setCurrentIndex(0);
      return;
    }

    if (currentIndex >= images.length) {
      setCurrentIndex(0);
    }
  }, [currentIndex, images.length]);

  useEffect(() => {
    if (autoplay && images.length > 1) {
      const interval = setInterval(() => {
        if (direction === 'ltr') {
          goPrev();
        } else {
          goNext();
        }
      }, autoplaySpeed);
      return () => clearInterval(interval);
    }
  }, [autoplay, autoplaySpeed, direction, goNext, goPrev, images.length]);

  if (images.length === 0) return null;

  const onTouchStart = (event) => {
    if (!swipeEnabled || images.length <= 1) return;
    setTouchEndX(null);
    setTouchStartX(event.touches[0].clientX);
  };

  const onTouchMove = (event) => {
    if (!swipeEnabled || images.length <= 1) return;
    setTouchEndX(event.touches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!swipeEnabled || images.length <= 1 || touchStartX === null || touchEndX === null) return;
    const swipeDelta = touchStartX - touchEndX;
    if (Math.abs(swipeDelta) >= 40) {
      if (swipeDelta > 0) {
        goNext();
      } else {
        goPrev();
      }
    }
    setTouchStartX(null);
    setTouchEndX(null);
  };

  return (
    <div
      className={`relative w-full overflow-hidden bg-white ${fullHeight ? 'h-full' : 'h-20 sm:h-24 md:h-32'}`}
      style={fullHeight ? { minHeight: `${minHeight}px` } : {}}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="flex h-full transition-transform duration-700 ease-out"
        style={{
          width: `${images.length * 100}%`,
          transform: `translateX(-${currentIndex * (100 / images.length)}%)`
        }}
      >
        {images.map((image, index) => {
          const link = (links[index] || '').trim();
          const mobileImage = mobileImages[index];
          const img = (
            // <picture> swaps in a dedicated mobile-cropped image below the md
            // breakpoint (768px, matches Tailwind's sm/md split used for these boxes'
            // heights) instead of stretching the desktop crop into a much narrower box
            // - a single crop can't fit both shapes without cutting off side content.
            // Falls back to the desktop image everywhere if no mobile crop was uploaded
            // (older banners predating this feature).
            <picture className="block w-full h-full">
              {mobileImage && mobileImage !== image && (
                <source media="(max-width: 767px)" srcSet={mobileImage} />
              )}
              <img
                src={image}
                alt={`Slide ${index + 1}`}
                // Rolled back to object-cover (fills the box, crops overflow) on request -
                // object-contain avoided cropping but left most uploaded banners as a thin
                // sliver surrounded by white space, since this box is a much wider/shorter
                // shape than typical banner designs. The real fix is cropping each image to
                // roughly match this box's shape before upload (done for the necklace image
                // already) - object-cover is the right default once images are shaped for it.
                className="w-full h-full object-cover"
                // NOT lazy: these slides sit inside a flex row shifted with a CSS transform
                // (not real scrolling) to animate between banners. The browser's lazy-load
                // heuristic checks each image's position at mount time relative to the
                // viewport, gets confused by the transform-based layout, and then never
                // loads the image at all - not even the first, always-visible slide. These
                // are also always above-the-fold hero content, so eager loading is correct
                // regardless of the bug.
              />
            </picture>
          );
          return (
            <div key={index} className="h-full" style={{ width: `${100 / images.length}%` }}>
              {!link ? (
                img
              ) : /^https?:\/\//i.test(link) ? (
                <a href={link} target="_blank" rel="noopener noreferrer" className="block h-full cursor-pointer">
                  {img}
                </a>
              ) : (
                <Link to={link} className="block h-full cursor-pointer">
                  {img}
                </Link>
              )}
            </div>
          );
        })}
      </div>
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? 'bg-white w-8' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Home = () => {
  const HOME_PRODUCTS_LIMIT = 48;
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryImageErrors, setCategoryImageErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const { formatPrice } = useCurrency();
  const { designConfig } = useDesign();
  const { track } = useLoading();

  const getAssetUrl = (assetPath) => {
    if (!assetPath) return '';
    if (/^https?:\/\//i.test(assetPath)) return assetPath;

    const API_BASE = process.env.REACT_APP_API_URL || '';
    const normalizedInput = assetPath.startsWith('/uploads/') ? `/api${assetPath}` : assetPath;
    const normalizedPath = normalizedInput.startsWith('/') ? normalizedInput : `/${normalizedInput}`;
    return `${API_BASE}${normalizedPath}`;
  };

  const extractProductsList = (json) => {
    if (!json) return [];
    if (Array.isArray(json.products)) return json.products;
    if (Array.isArray(json.data)) return json.data;
    if (Array.isArray(json.data?.products)) return json.data.products;
    return [];
  };

  const fetchCategories = useCallback(async () => {
    try {
      await track(async () => {
        const response = await fetch('/api/categories');

        if (!response.ok) {
          throw new Error(`Failed to fetch categories (HTTP ${response.status})`);
        }

        const data = await response.json();

        if (!data?.success) {
          throw new Error(data?.message || 'Failed to fetch categories');
        }

        setCategories(data.data?.categories || []);
      });
    } catch (error) {
      console.error('❌ [Home] Error fetching categories:', error);
      setCategories([]);
    }
  }, [track]);

  const fetchFeaturedProducts = useCallback(async () => {
    try {
      setLoading(true);
      await track(async () => {
        const API_BASE = process.env.REACT_APP_API_URL || '';
        const url = `${API_BASE}/api/products?page=1&limit=${HOME_PRODUCTS_LIMIT}`;
        console.log('🛍️ [Home] Fetching products from:', url);

        const response = await fetch(url);
        console.log('🛍️ [Home] Response status:', response.status);

        if (!response.ok) {
          console.error('❌ [Home] Response not OK:', response.status);
          throw new Error(`Failed to fetch products (HTTP ${response.status})`);
        }

        const data = await response.json();
        if (!data?.success) {
          console.error('❌ [Home] API returned success: false:', data?.message);
          throw new Error(data?.message || 'Failed to fetch products');
        }

        const productsList = extractProductsList(data).slice(0, HOME_PRODUCTS_LIMIT);

        console.log('✅ [Home] Setting home products:', productsList.length);
        setFeaturedProducts(productsList);
      });
    } catch (error) {
      console.error('❌ [Home] Error fetching featured products:', error);
      console.error('❌ [Home] Error details:', {
        message: error.message,
        stack: error.stack
      });
      setFeaturedProducts([]);
      setPageError(error?.message || 'Failed to load home page data');
    } finally {
      setLoading(false);
    }
  }, [HOME_PRODUCTS_LIMIT, track]);

  useEffect(() => {
    fetchFeaturedProducts();
    fetchCategories();
  }, [fetchCategories, fetchFeaturedProducts]);

  // Images and their click-through links are stored as two separate parallel
  // comma-separated strings (e.g. hero_banner_images / hero_banner_links) matched by
  // position - splitting them together (rather than each with its own .filter(Boolean))
  // keeps a link lined up with the correct image even if an earlier slide has no link.
  // mobileImagesRaw is optional and index-aligned with imagesRaw - a blank entry (or a
  // shorter list) means "no dedicated mobile crop for this slide, fall back to the
  // desktop image" (see BANNER_SLOTS / uploadCroppedPairs in AdminSettings.js).
  const parseImageLinkPairs = (imagesRaw, linksRaw, mobileImagesRaw) => {
    const imageList = (imagesRaw || '').trim() ? imagesRaw.split(',').map((u) => u.trim()) : [];
    const linkList = (linksRaw || '').trim() ? linksRaw.split(',').map((u) => u.trim()) : [];
    const mobileList = (mobileImagesRaw || '').trim() ? mobileImagesRaw.split(',').map((u) => u.trim()) : [];
    const images = [];
    const mobileImages = [];
    const links = [];
    imageList.forEach((url, i) => {
      if (!url) return;
      const desktopUrl = getAssetUrl(url);
      images.push(desktopUrl);
      links.push(linkList[i] || '');
      mobileImages.push(mobileList[i] ? getAssetUrl(mobileList[i]) : desktopUrl);
    });
    return { images, links, mobileImages };
  };

  const heroSale = useMemo(
    () => parseImageLinkPairs(designConfig.hero_banner_images, designConfig.hero_banner_links, designConfig.hero_banner_images_mobile),
    [designConfig.hero_banner_images, designConfig.hero_banner_links, designConfig.hero_banner_images_mobile]
  );
  const heroSaleImages = heroSale.images;
  const heroSaleMobileImages = heroSale.mobileImages;
  const heroSaleLinks = heroSale.links;

  const heroLeft = useMemo(
    () => parseImageLinkPairs(designConfig.hero_left_image, designConfig.hero_left_links, designConfig.hero_left_image_mobile),
    [designConfig.hero_left_image, designConfig.hero_left_links, designConfig.hero_left_image_mobile]
  );
  const heroLeftImages = heroLeft.images;
  const heroLeftMobileImages = heroLeft.mobileImages;
  const heroLeftLinks = heroLeft.links;

  const newArrival = useMemo(
    () => parseImageLinkPairs(designConfig.hero_right_image, designConfig.hero_right_links, designConfig.hero_right_image_mobile),
    [designConfig.hero_right_image, designConfig.hero_right_links, designConfig.hero_right_image_mobile]
  );
  const newArrivalImages = newArrival.images;
  const newArrivalMobileImages = newArrival.mobileImages;
  const newArrivalLinks = newArrival.links;

  const specialOffer = useMemo(
    () => parseImageLinkPairs(designConfig.special_offer_banner_image, designConfig.special_offer_links, designConfig.special_offer_banner_image_mobile),
    [designConfig.special_offer_banner_image, designConfig.special_offer_links, designConfig.special_offer_banner_image_mobile]
  );
  const specialOfferImages = specialOffer.images;
  const specialOfferMobileImages = specialOffer.mobileImages;
  const specialOfferLinks = specialOffer.links;

  const parseBool = (value, fallback = true) => String(value ?? (fallback ? 'true' : 'false')) === 'true';
  const parseSpeed = (value, fallback = 4000) => {
    const parsed = parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed >= 1500) return parsed;
    return fallback;
  };

  const heroBannerSettings = {
    autoplay: parseBool(designConfig.hero_banner_autoplay, true),
    autoplaySpeed: parseSpeed(designConfig.hero_banner_autoplay_speed, 4000),
    direction: String(designConfig.hero_banner_scroll_direction || 'rtl') === 'ltr' ? 'ltr' : 'rtl',
    swipeEnabled: parseBool(designConfig.hero_banner_swipe_enabled, true)
  };

  const heroLeftSettings = {
    autoplay: parseBool(designConfig.hero_left_autoplay, true),
    autoplaySpeed: parseSpeed(designConfig.hero_left_autoplay_speed, 4000),
    direction: String(designConfig.hero_left_scroll_direction || 'rtl') === 'ltr' ? 'ltr' : 'rtl',
    swipeEnabled: parseBool(designConfig.hero_left_swipe_enabled, true)
  };

  const heroRightSettings = {
    autoplay: parseBool(designConfig.hero_right_autoplay, true),
    autoplaySpeed: parseSpeed(designConfig.hero_right_autoplay_speed, 4000),
    direction: String(designConfig.hero_right_scroll_direction || 'rtl') === 'ltr' ? 'ltr' : 'rtl',
    swipeEnabled: parseBool(designConfig.hero_right_swipe_enabled, true)
  };

  const specialOfferSettings = {
    autoplay: parseBool(designConfig.special_offer_autoplay, true),
    autoplaySpeed: parseSpeed(designConfig.special_offer_autoplay_speed, 4000),
    direction: String(designConfig.special_offer_scroll_direction || 'rtl') === 'ltr' ? 'ltr' : 'rtl',
    swipeEnabled: parseBool(designConfig.special_offer_swipe_enabled, true)
  };

  const heroTopEnabled = parseBool(designConfig.hero_banner_enabled, true);
  const heroLeftEnabled = parseBool(designConfig.hero_left_enabled, true);
  const heroRightEnabled = parseBool(designConfig.hero_right_enabled, true);
  const specialOfferEnabled = parseBool(designConfig.special_offer_enabled, true);

  const hasBagCategory = categories.some((cat) => /bag/i.test(String(cat?.name || '')));
  const displayCategories = hasBagCategory
    ? categories
    : [{ id: 'bags_virtual', name: 'Bags', image: null }, ...categories];

  const bagCategoryCards = [
    { name: 'School Bags', link: '/products?category=22' },
    { name: 'Ladies Handbags', link: '/products?category=23' },
    { name: 'Wallets', link: '/products?category=24' },
    { name: 'Gents Handbags', link: '/products?category=25' },
    { name: 'Model Bags', link: '/products?category=26' }
  ];

  const lowerHeroSectionsCount = (heroLeftEnabled ? 1 : 0) + (heroRightEnabled ? 1 : 0);

  const getPricing = (product) => {
    const originalPrice = (product.original_price !== null && product.original_price !== undefined && product.original_price !== '')
      ? parseFloat(product.original_price)
      : null;
    const currentPrice = parseFloat(product.price) || 0;
    const discountAmount = (product.discount !== null && product.discount !== undefined && product.discount !== '' && product.discount !== '0')
      ? parseFloat(product.discount)
      : 0;

    let discountPercent = 0;
    let showOriginalPrice = false;
    let hasDiscount = false;

    if (originalPrice && !isNaN(originalPrice) && originalPrice > 0 && originalPrice > currentPrice) {
      discountPercent = Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
      hasDiscount = discountPercent > 0 && discountPercent <= 100;
      showOriginalPrice = true;
    } else if (discountAmount && !isNaN(discountAmount) && discountAmount > 0 && currentPrice > 0) {
      const priceWithDiscount = currentPrice + discountAmount;
      discountPercent = Math.round((discountAmount / priceWithDiscount) * 100);
      hasDiscount = discountPercent > 0 && discountPercent <= 100;
      showOriginalPrice = false;
    }

    return {
      currentPrice,
      originalPrice,
      hasDiscount,
      discountPercent,
      showOriginalPrice
    };
  };

  if (pageError) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto max-w-full px-3 sm:px-4 pt-4">
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <p className="text-sm font-semibold text-red-800">Failed to load Home page</p>
            <p className="text-sm text-red-700 mt-1">{pageError}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: designConfig.background_color }}
    >
      {/* Hero + Promo (matches reference: sale banner + New Arrivals banner) */}
      <section className="bg-white">
        <div className="container mx-auto max-w-full px-3 sm:px-4 pt-3 sm:pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
            {heroTopEnabled && (
            <div className="lg:col-span-3 rounded-xl overflow-hidden shadow-sm border border-gray-100">
              {heroSaleImages.length > 0 ? (
                <div className="h-[100px] sm:h-[120px] md:h-[140px]">
                  <BannerCarousel images={heroSaleImages} mobileImages={heroSaleMobileImages} links={heroSaleLinks} fullHeight minHeight={100} {...heroBannerSettings} />
                </div>
              ) : (
                <div
                  className="h-[100px] sm:h-[120px] md:h-[140px] flex items-center justify-center text-center px-4"
                  style={{
                    background: `linear-gradient(135deg, ${designConfig.primary_color} 0%, ${designConfig.accent_color} 100%)`,
                    color: 'white'
                  }}
                >
                  <div className="text-sm sm:text-base md:text-lg font-bold">{designConfig.hero_banner_text || 'WELCOME TO OUR STORE'}</div>
                </div>
              )}
            </div>
            )}

            {heroLeftEnabled && (
            <div className={`${heroRightEnabled ? 'lg:col-span-2' : 'lg:col-span-3'} rounded-xl overflow-hidden shadow-sm border border-gray-100`}>
              {heroLeftImages.length > 0 ? (
                <div className="h-[140px] sm:h-[180px] md:h-[240px] lg:h-[260px]">
                  <BannerCarousel images={heroLeftImages} mobileImages={heroLeftMobileImages} links={heroLeftLinks} fullHeight minHeight={180} {...heroLeftSettings} />
                </div>
              ) : (
                <div
                  className="h-[140px] sm:h-[180px] md:h-[240px] lg:h-[260px] flex items-center justify-center text-center px-4"
                  style={{
                    background: `linear-gradient(135deg, ${designConfig.primary_color} 0%, ${designConfig.accent_color} 100%)`,
                    color: 'white'
                  }}
                >
                  <div>
                    <div className="text-xs sm:text-sm tracking-widest opacity-90">{designConfig.hero_left_text || 'NEW YEAR SALE'}</div>
                    <div className="text-3xl sm:text-4xl md:text-5xl font-extrabold mt-2">10–50% OFF</div>
                  </div>
                </div>
              )}
            </div>
            )}

            {heroRightEnabled && (
            <div className={`${heroLeftEnabled ? '' : 'lg:col-span-3'} rounded-xl overflow-hidden shadow-sm border border-gray-100`}>
              {newArrivalImages.length > 0 ? (
                <div className="h-[140px] sm:h-[180px] md:h-[240px] lg:h-[260px] relative">
                  <BannerCarousel images={newArrivalImages} mobileImages={newArrivalMobileImages} links={newArrivalLinks} fullHeight minHeight={180} {...heroRightSettings} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Link
                      to="/products"
                      className="px-5 py-2 rounded-full text-white font-semibold shadow-lg"
                      style={{ backgroundColor: designConfig.primary_color }}
                    >
                      Shop Now
                    </Link>
                  </div>
                </div>
              ) : (
                <div
                  className="h-[140px] sm:h-[180px] md:h-[240px] lg:h-[260px] flex items-center justify-center text-center px-4"
                  style={{
                    background: `linear-gradient(135deg, ${designConfig.secondary_color} 0%, ${designConfig.accent_color} 100%)`,
                    color: 'white'
                  }}
                >
                  <div>
                    <div className="text-2xl sm:text-3xl font-extrabold">{designConfig.hero_right_text || 'New Arrivals'}</div>
                    <Link
                      to="/products"
                      className="inline-block mt-3 px-5 py-2 rounded-full text-white font-semibold"
                      style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}
                    >
                      Shop Now ›
                    </Link>
                  </div>
                </div>
              )}
            </div>
            )}

            {lowerHeroSectionsCount === 0 && !heroTopEnabled && (
              <div className="lg:col-span-3 rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                Hero banner sections are disabled in settings.
              </div>
            )}
          </div>

          {/* Category strip (mobile scroll / desktop row) */}
          {displayCategories.length > 0 && (
            <div className="mt-4 sm:mt-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base sm:text-lg font-semibold" style={{ color: designConfig.text_color }}>Browse Categories</h2>
                <Link to="/categories" className="text-sm font-semibold" style={{ color: designConfig.primary_color }}>View All</Link>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                {displayCategories.slice(0, 10).map((cat) => (
                  <Link
                    key={cat.id}
                    to={cat.id === 'bags_virtual' ? '/products?search=bag' : `/products?category=${cat.id}`}
                    className="flex-shrink-0 w-[72px] sm:w-[84px]"
                  >
                    <div className="flex flex-col items-center">
                      <div
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center shadow-sm border border-gray-100 overflow-hidden"
                        style={{ backgroundColor: '#FFFFFF' }}
                      >
                        {cat.image && !categoryImageErrors[cat.id] ? (
                          <img
                            src={getAssetUrl(cat.image)}
                            alt={cat.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={() => {
                              setCategoryImageErrors((prev) => ({
                                ...prev,
                                [cat.id]: true
                              }));
                            }}
                          />
                        ) : (
                          <span className="text-lg font-bold" style={{ color: designConfig.primary_color }}>
                            {(cat.name || '?').trim().charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[11px] sm:text-xs text-center leading-tight line-clamp-2" style={{ color: designConfig.text_color }}>
                        {cat.name}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Homepage featured bag category cards */}
          <div className="mt-5 sm:mt-7">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base sm:text-lg font-semibold" style={{ color: designConfig.text_color }}>Shop Bags</h2>
              <Link to="/products?category=21" className="text-sm font-semibold" style={{ color: designConfig.primary_color }}>View All Bags</Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
              {bagCategoryCards.map((bagCard) => (
                <Link
                  key={bagCard.name}
                  to={bagCard.link}
                  className="rounded-xl border border-gray-100 bg-white px-3 py-3 text-center text-sm font-semibold shadow-sm transition-all hover:shadow-md"
                  style={{ color: designConfig.text_color }}
                >
                  {bagCard.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured products block (matches reference: promo graphic + product cards) */}
      <section className="py-6 sm:py-10 bg-white">
        <div className="container mx-auto max-w-full px-3 sm:px-4">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold" style={{ color: designConfig.text_color }}>All Products</h2>
              <p className="text-sm text-gray-600">Browse our full collection</p>
            </div>
            <Link to="/products" className="text-sm font-semibold" style={{ color: designConfig.primary_color }}>All Products</Link>
          </div>

          {loading ? (
            <ProductGridSkeleton count={16} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-2 sm:gap-3" />
          ) : (
            <div>
              {/* Compact promo banner (prevents empty left column on tall grids) */}
              {specialOfferEnabled && (
              <div className="relative rounded-2xl overflow-hidden px-5 py-5 sm:px-7 sm:py-6 flex items-center justify-between gap-4 mb-4">
                {specialOfferImages.length > 0 && (
                  <div className="absolute inset-0">
                    <BannerCarousel images={specialOfferImages} mobileImages={specialOfferMobileImages} links={specialOfferLinks} fullHeight minHeight={120} {...specialOfferSettings} />
                  </div>
                )}
                <div
                  className="absolute inset-0"
                  style={{
                    background: specialOfferImages.length > 0
                      ? 'linear-gradient(90deg, rgba(17, 24, 39, 0.5) 0%, rgba(17, 24, 39, 0.18) 24%, rgba(17, 24, 39, 0) 55%)'
                      : `linear-gradient(135deg, ${designConfig.primary_color} 0%, ${designConfig.accent_color} 100%)`,
                    opacity: 1,
                    pointerEvents: 'none'
                  }}
                />

                <div className="relative text-white" style={{ textShadow: '0 1px 4px rgba(0, 0, 0, 0.45)' }}>
                  <div className="text-xs sm:text-sm tracking-widest opacity-90">{designConfig.special_offer_label || 'SPECIAL OFFER'}</div>
                  <div className="text-4xl sm:text-5xl font-extrabold leading-none">{designConfig.special_offer_percent_text || '10%'}</div>
                  <div className="text-sm sm:text-base font-semibold mt-1">{designConfig.special_offer_off_text || 'OFF'}</div>
                </div>
                <Link
                  to={designConfig.special_offer_button_link || '/products'}
                  className="relative z-10 shrink-0 inline-flex items-center justify-center min-w-[124px] h-11 bg-white/20 hover:bg-white/30 border border-white/30 px-5 rounded-full font-semibold text-white text-sm transition-colors"
                >
                  {designConfig.special_offer_button_text || 'Shop Now'}
                </Link>
              </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-2 sm:gap-3">
                {featuredProducts.map((product, index) => {
                  const pricing = getPricing(product);
                  return (
                    <div
                      key={product.id}
                      className="group bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                    >
                      <ProductMediaWithSwatches
                        product={product}
                        linkClassName="block"
                        containerClassName="aspect-square bg-white flex items-center justify-center"
                        imgClassName="w-full h-full object-contain p-2 group-hover:scale-[1.02] transition-transform duration-200"
                      />

                      <div className="px-2.5 pb-2.5">
                        <Link to={`/products/${product.id}`}>
                          <div className="mt-2 text-xs sm:text-sm font-semibold text-gray-900 line-clamp-2 min-h-[2.25rem]">
                            {product.name}
                          </div>
                        </Link>

                        <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                          <span className="text-sm sm:text-base font-bold" style={{ color: designConfig.primary_color }}>
                            {formatPrice(pricing.currentPrice)}
                          </span>
                          {pricing.showOriginalPrice && pricing.originalPrice && (
                            <span className="text-[11px] text-gray-500 line-through">{formatPrice(pricing.originalPrice)}</span>
                          )}
                        </div>

                        {pricing.hasDiscount && pricing.discountPercent > 0 && (
                          <div className="mt-1 text-[11px] font-semibold text-red-600">
                            {pricing.discountPercent}% OFF
                          </div>
                        )}

                        <div className="mt-2 text-[11px] text-gray-600">
                          {/* Was a hardcoded "Free Delivery" claim shown on every card
                              regardless of the actual shipping fee/threshold config -
                              replaced with the delivery estimate, which was already
                              being computed correctly right next to it. */}
                          <span className="text-blue-600 font-semibold">
                            Delivery expected {getDeliveryOptions(product).express.deliveryText.replace(/^express\s*/i, 'by ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Best Sellers Section */}
      <section className="py-8 sm:py-12 bg-white">
        <div className="container mx-auto max-w-full px-3 sm:px-4">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
              BEST SELLERS
            </h2>
            <div className="w-16 sm:w-20 md:w-24 h-0.5 sm:h-1 mx-auto rounded-full" style={{ 
              background: `linear-gradient(to right, ${designConfig.primary_color}, ${designConfig.secondary_color})`
            }}></div>
          </div>
          
          {loading ? (
            <ProductGridSkeleton count={4} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5" />
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5 overflow-x-auto">
              {featuredProducts.slice(0, 4).map((product, index) => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                    <img
                      src={product.image || '/api/placeholder/400/300'}
                      alt={product.name}
                      className="w-full h-full object-contain p-1 sm:p-1.5 group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Watermark */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-10 transition-opacity">
                      <span className="text-6xl font-bold text-gray-400" style={{ fontFamily: 'serif' }}>
                        {designConfig.site_name?.charAt(0) || 'V'}
                      </span>
                    </div>
                  </div>
                  <div className="p-2 sm:p-3">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-2 line-clamp-2 min-h-[2rem] sm:min-h-[2.5rem]">
                      {product.name}
                    </h3>
                    <p className="text-base sm:text-lg font-bold" style={{ color: designConfig.primary_color }}>
                      {formatPrice(product.price)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No best sellers available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      <RecentlyViewed />

      {/* Features Section - Elegant Style */}
      <section 
        className="py-24"
        style={{ backgroundColor: 'white' }}
      >
        <div className="container mx-auto max-w-full px-4">
          <div className="text-center mb-20">
            <div className="inline-block mb-6">
              <div className="flex items-center justify-center gap-2">
                <div className="h-px w-12" style={{ backgroundColor: designConfig.primary_color }}></div>
                <span className="text-sm font-semibold tracking-widest uppercase" style={{ color: designConfig.primary_color }}>
                  Why Choose Us
                </span>
                <div className="h-px w-12" style={{ backgroundColor: designConfig.primary_color }}></div>
              </div>
            </div>
            <h2 
              className="text-5xl md:text-6xl font-light mb-6 tracking-wide"
              style={{ 
                color: designConfig.text_color,
                fontFamily: 'serif'
              }}
            >
              Our Promise
            </h2>
            <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed" style={{ 
              color: designConfig.text_color,
              opacity: 0.7
            }}>
              Experience premium service and unmatched quality
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            {[1, 2, 3].map((num) => (
              <div key={num} className="text-center group">
                <div 
                  className="text-white w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-all duration-500 shadow-xl"
                  style={{ 
                    background: `linear-gradient(135deg, ${designConfig.primary_color}, ${designConfig.accent_color})`
                  }}
                >
                  {num === 1 && (
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {num === 2 && (
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {num === 3 && (
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  )}
                </div>
                <h3 
                  className="text-2xl font-light mb-4 tracking-wide"
                  style={{ 
                    color: designConfig.text_color,
                    fontFamily: 'serif'
                  }}
                >
                  {designConfig[`feature_${num}_title`] || `Feature ${num}`}
                </h3>
                <p 
                  className="leading-relaxed text-base"
                  style={{ 
                    color: designConfig.text_color,
                    opacity: 0.7
                  }}
                >
                  {designConfig[`feature_${num}_description`] || 'Feature description'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
